import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      // Distinguish real API errors from network / CORS failures so users see
      // an actionable message instead of "Something went wrong".
      let msg = formatApiErrorDetail(e.response?.data?.detail);
      if (!e.response) {
        const browserMsg = e.message || "";
        if (/Network Error|ERR_NETWORK|ERR_CONNECTION|CORS/i.test(browserMsg)) {
          msg = (
            "Couldn't reach the API. This usually means a network glitch or a " +
            "cross-origin (CORS) misconfiguration. Try again; if it persists, " +
            "check the browser console for CORS errors."
          );
        } else if (browserMsg) {
          msg = browserMsg;
        }
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-zinc-900">
        <img
          alt=""
          src="https://static.prod-images.emergentagent.com/jobs/c07931b4-3788-4b0f-a1d6-1d0f213d4d21/images/1543c299ca605dcfc3aaffd5e2c849b2507e1d3b625fff18b0ac0a1d9c652386.png"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <Buildings size={18} color="#1d4ed8" weight="bold" />
            </div>
            <div className="font-display font-black tracking-tight">GRANTFLOW</div>
          </div>
          <div>
            <div className="gf-overline text-white/70">Federal grants, streamlined</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-4 max-w-md">
              Search. Draft. Submit.
            </h1>
            <p className="text-white/80 mt-4 max-w-md text-sm leading-relaxed">
              Discover live US federal grant opportunities, draft winning proposals with
              Claude Sonnet 4.5, and submit them to agency contacts — all in one workspace.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-zinc-50">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="gf-overline">Sign in</div>
          <h2 className="font-display font-bold text-3xl mt-2 tracking-tight">
            Welcome back.
          </h2>
          <p className="text-zinc-600 text-sm mt-2">
            Use your email and password to continue.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="gf-overline" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="gf-sharp mt-2 border-zinc-300 bg-white"
                placeholder="you@org.gov"
              />
            </div>
            <div>
              <Label className="gf-overline" htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="gf-sharp mt-2 border-zinc-300 bg-white"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 p-3"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="gf-sharp w-full bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="mt-6 text-sm text-zinc-600">
            No account?{" "}
            <Link to="/register" className="text-blue-700 font-semibold hover:underline" data-testid="link-register">
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
